(function () {
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class CartDrawer {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.sectionId;
      this.panel = el.querySelector('.cart-drawer__panel');
      this.busy = false;
      this.upsellCache = new Map();
      this.lastFocus = null;
      this.inerted = [];
      this.errorTimer = null;
      this.giftSyncing = false;
      this.giftFailed = false;
      this.bind();
      this.afterRender();
    }

    get inner() {
      return this.el.querySelector('[data-cart-drawer-inner]');
    }

    bind() {
      document.querySelectorAll('[data-cart-toggle]').forEach((t) => {
        t.setAttribute('aria-haspopup', 'dialog');
        t.setAttribute('aria-expanded', 'false');
      });

      document.addEventListener('click', (e) => {
        const toggle = e.target.closest && e.target.closest('[data-cart-toggle]');
        if (!toggle || this.el.contains(toggle)) return;
        e.preventDefault();
        this.lastFocus = toggle;
        this.open();
        this.refresh();
      });

      this.el.addEventListener('click', (e) => this.onClick(e));
      this.el.addEventListener('change', (e) => this.onChange(e));

      document.addEventListener('keydown', (e) => {
        if (!this.isOpen()) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'Tab') this.trapFocus(e);
      });

      document.addEventListener('submit', (e) => this.onProductSubmit(e));
      document.addEventListener('cart:refresh', () => this.refresh());
      document.addEventListener('cart:open', () => { this.open(); this.refresh(); });
    }

    isOpen() {
      return this.el.classList.contains('is-open');
    }

    open() {
      if (this.isOpen()) return;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((n) => n.setAttribute('aria-expanded', 'true'));
      this.setInert(true);
      const closeBtn = this.el.querySelector('.cart-drawer__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((n) => n.setAttribute('aria-expanded', 'false'));
      this.setInert(false);
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }

    setInert(on) {
      if (on) {
        const section = this.el.closest('.shopify-section') || this.el;
        this.inerted = Array.from(document.body.children).filter(
          (n) => n !== section && n.tagName !== 'SCRIPT' && !n.hasAttribute('inert')
        );
        this.inerted.forEach((n) => n.setAttribute('inert', ''));
      } else {
        this.inerted.forEach((n) => n.removeAttribute('inert'));
        this.inerted = [];
      }
    }

    trapFocus(e) {
      const nodes = Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')
      ).filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    async request(path, body) {
      const res = await fetch(ROOT + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, body, { sections: this.sectionId, sections_url: ROOT }))
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong');
      return data;
    }

    async refresh() {
      try {
        const res = await fetch(ROOT + '?sections=' + encodeURIComponent(this.sectionId), {
          headers: { Accept: 'application/json' }
        });
        const data = await res.json();
        this.renderSection(data[this.sectionId]);
      } catch (err) {
        console.error(err);
      }
    }

    renderSection(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-drawer-inner]');
      const current = this.inner;
      if (!next || !current) return;
      const oldBody = current.querySelector('[data-cart-drawer-body]');
      const scroll = oldBody ? oldBody.scrollTop : 0;
      const focusedInDrawer = this.el.contains(document.activeElement);
      current.replaceWith(next);
      const newBody = next.querySelector('[data-cart-drawer-body]');
      if (newBody) newBody.scrollTop = scroll;
      if (focusedInDrawer && this.isOpen()) {
        const closeBtn = this.el.querySelector('.cart-drawer__close');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }
      this.afterRender();
    }

    afterRender() {
      const count = Number((this.inner && this.inner.dataset.cartItemCount) || 0);
      document.querySelectorAll('[data-cart-count]').forEach((n) => {
        n.textContent = count;
        n.hidden = count === 0;
      });
      this.loadUpsell();
      this.syncGift();
    }

    /* Free gift: add it once the paid item count reaches the threshold, remove it below. */
    async syncGift() {
      const inner = this.inner;
      if (!inner || this.giftSyncing || this.giftFailed) return;
      const variantId = Number(inner.dataset.giftVariantId || 0);
      const giftQty = Number(inner.dataset.giftQty || 0);
      const key = inner.dataset.giftKey;
      const threshold = Math.max(1, Number(inner.dataset.giftThreshold || 1));
      const paid = Number(inner.dataset.paidCount || 0);
      const qualifies = variantId && paid >= threshold;

      let path = null;
      let body = null;
      if (qualifies && giftQty === 0) {
        path = 'cart/add.js';
        body = { items: [{ id: variantId, quantity: 1 }] };
      } else if (qualifies && giftQty > 1 && key) {
        path = 'cart/change.js';
        body = { id: key, quantity: 1 };
      } else if (!qualifies && giftQty > 0 && key) {
        path = 'cart/change.js';
        body = { id: key, quantity: 0 };
      }
      if (!path) return;

      this.giftSyncing = true;
      try {
        const data = await this.request(path, body);
        this.giftSyncing = false;
        this.renderSection(data.sections && data.sections[this.sectionId]);
      } catch (err) {
        console.error(err);
        this.giftFailed = true;
      } finally {
        this.giftSyncing = false;
      }
    }

    async loadUpsell() {
      const inner = this.inner;
      const box = inner && inner.querySelector('[data-cart-upsell]');
      if (!box) return;
      const track = box.querySelector('[data-cart-upsell-track]');
      const limit = Number(box.dataset.limit || 4);
      const label = box.dataset.buttonLabel;
      const inCart = (inner.dataset.cartProductIds || '').split(',').filter(Boolean);

      if (box.dataset.source === 'recommendations') {
        const pid = box.dataset.productId;
        if (!pid) return;
        let html = this.upsellCache.get(pid);
        if (html === undefined) {
          html = await this.fetchRecs(pid, 'complementary');
          if (!this.countCards(html, inCart)) html = await this.fetchRecs(pid, 'related');
          this.upsellCache.set(pid, html);
        }
        if (!document.contains(box)) return;
        track.innerHTML = html;
      }

      let shown = 0;
      track.querySelectorAll('[data-upsell-card]').forEach((card) => {
        if (inCart.includes(card.dataset.productId) || shown >= limit) card.remove();
        else shown += 1;
      });
      if (label) track.querySelectorAll('[data-upsell-add-label]').forEach((n) => { n.textContent = label; });
      box.hidden = shown === 0;
    }

    countCards(html, inCart) {
      const doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
      return Array.from(doc.querySelectorAll('[data-upsell-card]')).filter((c) => !inCart.includes(c.dataset.productId)).length;
    }

    async fetchRecs(pid, intent) {
      try {
        const url = ROOT + 'recommendations/products?product_id=' + encodeURIComponent(pid) + '&limit=10&intent=' + intent + '&section_id=cart-drawer-upsell';
        const res = await fetch(url);
        if (!res.ok) return '';
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const results = doc.querySelector('[data-upsell-results]');
        return results ? results.innerHTML.trim() : '';
      } catch (err) {
        return '';
      }
    }

    onClick(e) {
      if (e.target.closest('[data-cart-drawer-close]')) {
        this.close();
        return;
      }
      const qtyBtn = e.target.closest('[data-cart-qty]');
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (qtyBtn || removeBtn) {
        const item = (qtyBtn || removeBtn).closest('[data-cart-item-key]');
        if (!item) return;
        const input = item.querySelector('[data-cart-qty-input]');
        const qty = removeBtn ? 0 : Math.max(0, Number(input.value || 0) + Number(qtyBtn.dataset.cartQty));
        this.changeItem(item, qty);
        return;
      }
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) {
        e.preventDefault();
        const card = addBtn.closest('[data-upsell-card]');
        const select = card && card.querySelector('[data-upsell-variant]');
        this.addUpsell(addBtn, Number(select ? select.value : addBtn.dataset.variantId));
      }
    }

    onChange(e) {
      const input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      const item = input.closest('[data-cart-item-key]');
      if (item) this.changeItem(item, Math.max(0, Number(input.value) || 0));
    }

    async changeItem(item, quantity) {
      if (this.busy) return;
      this.busy = true;
      item.classList.add('is-pending');
      try {
        const data = await this.request('cart/change.js', { id: item.dataset.cartItemKey, quantity: quantity });
        this.renderSection(data.sections && data.sections[this.sectionId]);
      } catch (err) {
        await this.refresh();
        this.showError(err.message);
      } finally {
        this.busy = false;
      }
    }

    async addUpsell(btn, variantId) {
      if (this.busy || !variantId) return;
      this.busy = true;
      btn.disabled = true;
      btn.classList.add('is-loading');
      try {
        const data = await this.request('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
        this.renderSection(data.sections && data.sections[this.sectionId]);
      } catch (err) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        this.showError(err.message);
      } finally {
        this.busy = false;
      }
    }

    showError(message) {
      const box = this.inner && this.inner.querySelector('[data-cart-drawer-error]');
      if (!box || !message) return;
      box.textContent = message;
      box.hidden = false;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => { box.hidden = true; }, 5000);
    }

    async onProductSubmit(e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;
      const submitter = e.submitter;
      if (!submitter || submitter.getAttribute('name') !== 'add') return;
      e.preventDefault();

      const label = submitter.querySelector('[data-add-to-cart-label]');
      const labelText = label ? label.textContent : '';
      const errorEl = form.querySelector('[data-add-to-cart-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      submitter.disabled = true;
      if (label && this.el.dataset.addingLabel) label.textContent = this.el.dataset.addingLabel;

      try {
        const fd = new FormData(form);
        fd.append('sections', this.sectionId);
        fd.append('sections_url', ROOT);
        const res = await fetch(ROOT + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || '');
        this.lastFocus = submitter;
        this.renderSection(data.sections && data.sections[this.sectionId]);
        this.open();
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message || errorEl.dataset.genericError || '';
          errorEl.hidden = false;
        } else {
          window.location.href = form.action || ROOT + 'cart';
        }
      } finally {
        submitter.disabled = false;
        if (label) label.textContent = labelText;
      }
    }
  }

  function init() {
    const el = document.querySelector('[data-cart-drawer]');
    if (el && !window.cartDrawer) window.cartDrawer = new CartDrawer(el);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
